import React, { useEffect, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { ShieldCheck, Trash2, Plus, Leaf, RefreshCw, Newspaper, Users, Mail, FolderSync } from "lucide-react";
import GlassCard from "../../components/ui-custom/GlassCard";
import Reveal from "../../components/ui-custom/Reveal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useAuth } from "../../context/AuthContext";
import { formatDateTime } from "../../lib/formatDate";
import { formatNumber } from "../../lib/formatNumber";
import { ADMIN_URL } from "../../qpi/site";

const RACI_LABELS = { R: "Responsible", A: "Accountable", C: "Consulted", I: "Informed" };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const AdminList = () => {
  const [admins, setAdmins] = useState(null);
  const [bootstrap, setBootstrap] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios
      .get(`${API}/admin/admins`, { withCredentials: true })
      .then((res) => {
        setAdmins(res.data.items || []);
        setBootstrap(res.data.bootstrap || []);
      })
      .catch(() => setError("Could not load the admin list."));
  };

  useEffect(load, []);

  const addAdmin = (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setBusy(true);
    setError("");
    axios
      .post(`${API}/admin/admins`, { email: newEmail.trim() }, { withCredentials: true })
      .then(() => {
        setNewEmail("");
        load();
      })
      .catch((err) => setError(err.response?.data?.detail || "Could not add that admin."))
      .finally(() => setBusy(false));
  };

  const removeAdmin = (email) => {
    setBusy(true);
    setError("");
    axios
      .delete(`${API}/admin/admins/${encodeURIComponent(email)}`, { withCredentials: true })
      .then(load)
      .catch((err) => setError(err.response?.data?.detail || "Could not remove that admin."))
      .finally(() => setBusy(false));
  };

  return (
    <GlassCard className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <ShieldCheck size={20} className="text-qcyan" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Admins</h3>
          <p className="text-xs text-muted-foreground">Can manage sustainability sites and other admins.</p>
        </div>
      </div>

      {admins === null && <p className="text-sm text-muted-foreground">Loading…</p>}

      <ul className="space-y-2 mb-5">
        {admins?.map((email) => (
          <li
            key={email}
            className="flex items-center justify-between gap-3 text-sm border border-border/10 rounded-lg px-4 py-2.5"
          >
            <span className="text-foreground">{email}</span>
            {bootstrap.includes(email) ? (
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Permanent</span>
            ) : (
              <button
                onClick={() => removeAdmin(email)}
                disabled={busy}
                className="text-muted-foreground hover:text-destructive transition-colors"
              >
                <Trash2 size={15} />
              </button>
            )}
          </li>
        ))}
      </ul>

      <form onSubmit={addAdmin} className="flex gap-2">
        <Input
          type="email"
          placeholder="name@qualitypower.co.in"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
        />
        <Button type="submit" className="btn-glow-primary gap-1.5 flex-none" disabled={busy}>
          <Plus size={15} /> Add
        </Button>
      </form>
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
    </GlassCard>
  );
};

const SiteList = () => {
  const [sites, setSites] = useState(null);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios
      .get(`${API}/sustainability/sites`)
      .then((res) => setSites(res.data.items || []))
      .catch(() => setError("Could not load the site list."));
  };

  useEffect(load, []);

  const addSite = (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    setError("");
    axios
      .post(`${API}/sustainability/sites`, { name: newName.trim() }, { withCredentials: true })
      .then(() => {
        setNewName("");
        load();
      })
      .catch((err) => setError(err.response?.data?.detail || "Could not create that site."))
      .finally(() => setBusy(false));
  };

  const removeSite = (siteId) => {
    setBusy(true);
    setError("");
    axios
      .delete(`${API}/sustainability/sites/${siteId}`, { withCredentials: true })
      .then(load)
      .catch((err) => setError(err.response?.data?.detail || "Could not remove that site."))
      .finally(() => setBusy(false));
  };

  return (
    <GlassCard className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <Leaf size={20} className="text-qcyan" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Sustainability Sites</h3>
          <p className="text-xs text-muted-foreground">
            Each site gets its own Google Sheet, viewable by all employees, editable by admins only.
          </p>
        </div>
      </div>

      {sites === null && <p className="text-sm text-muted-foreground">Loading…</p>}
      {sites && sites.length === 0 && <p className="text-sm text-muted-foreground mb-5">No sites yet.</p>}

      <ul className="space-y-2 mb-5">
        {sites?.map((s) => (
          <li
            key={s.site_id}
            className="flex items-center justify-between gap-3 text-sm border border-border/10 rounded-lg px-4 py-2.5"
          >
            <span className="text-foreground">{s.name}</span>
            <button
              onClick={() => removeSite(s.site_id)}
              disabled={busy}
              className="text-muted-foreground hover:text-destructive transition-colors"
            >
              <Trash2 size={15} />
            </button>
          </li>
        ))}
      </ul>

      <form onSubmit={addSite} className="flex gap-2">
        <Input placeholder="e.g. L-61 Factory" value={newName} onChange={(e) => setNewName(e.target.value)} />
        <Button type="submit" className="btn-glow-primary gap-1.5 flex-none" disabled={busy}>
          <Plus size={15} /> Add
        </Button>
      </form>
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
      <p className="text-xs text-muted-foreground mt-4">
        After adding a site, open its sheet in Google Drive (Shared Drive → Sustainability folder) to add tabs and
        data - it'll show up on the public Sustainability page automatically.
      </p>
    </GlassCard>
  );
};

const RaciCard = () => {
  const [sites, setSites] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [entries, setEntries] = useState([]);
  const [pickEmail, setPickEmail] = useState("");
  const [pickRaci, setPickRaci] = useState("R");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/sustainability/sites`).then((res) => {
      const items = res.data.items || [];
      setSites(items);
      if (items.length > 0) setSiteId(items[0].site_id);
    });
    axios
      .get(`${API}/admin/employees`, { withCredentials: true })
      .then((res) => setEmployees(res.data.items || []));
  }, []);

  const loadEntries = (id) => {
    if (!id) return;
    axios
      .get(`${API}/admin/sustainability/raci/${id}`, { withCredentials: true })
      .then((res) => setEntries(res.data.entries || []))
      .catch(() => setError("Could not load RACI assignments for that site."));
  };

  useEffect(() => {
    loadEntries(siteId);
  }, [siteId]);

  const saveEntries = (next) => {
    setBusy(true);
    setError("");
    axios
      .post(
        `${API}/admin/sustainability/raci/${siteId}`,
        { entries: next.map((e) => ({ email: e.email, raci: e.raci })) },
        { withCredentials: true }
      )
      .then((res) => setEntries(res.data.entries || []))
      .catch((err) => setError(err.response?.data?.detail || "Could not save RACI assignments."))
      .finally(() => setBusy(false));
  };

  const addEntry = (e) => {
    e.preventDefault();
    if (!pickEmail) return;
    if (entries.some((en) => en.email === pickEmail && en.raci === pickRaci)) return;
    saveEntries([...entries, { email: pickEmail, raci: pickRaci }]);
  };

  const removeEntry = (email, raci) => {
    saveEntries(entries.filter((en) => !(en.email === email && en.raci === raci)));
  };

  return (
    <GlassCard className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <Users size={20} className="text-qcyan" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Site RACI</h3>
          <p className="text-xs text-muted-foreground">
            Responsible / Accountable / Consulted / Informed per factory - shown as a photo ribbon on the public
            Sustainability page.
          </p>
        </div>
      </div>

      {sites === null && <p className="text-sm text-muted-foreground">Loading…</p>}
      {sites && sites.length === 0 && <p className="text-sm text-muted-foreground">Add a site first.</p>}

      {sites && sites.length > 0 && (
        <>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 block">
            Site
          </label>
          <select
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            className="w-full mb-5 rounded-lg bg-background border border-border/20 px-3 py-2 text-sm text-foreground"
          >
            {sites.map((s) => (
              <option key={s.site_id} value={s.site_id}>
                {s.name}
              </option>
            ))}
          </select>

          <ul className="space-y-2 mb-5">
            {entries.length === 0 && <li className="text-sm text-muted-foreground">No one assigned yet.</li>}
            {entries.map((en) => (
              <li
                key={en.email + en.raci}
                className="flex items-center justify-between gap-3 text-sm border border-border/10 rounded-lg px-4 py-2.5"
              >
                <span className="text-foreground">
                  {en.name} <span className="text-muted-foreground">— {en.raci} · {RACI_LABELS[en.raci]}</span>
                </span>
                <button
                  onClick={() => removeEntry(en.email, en.raci)}
                  disabled={busy}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>

          <form onSubmit={addEntry} className="flex flex-wrap gap-2">
            <select
              value={pickEmail}
              onChange={(e) => setPickEmail(e.target.value)}
              className="flex-1 min-w-[160px] rounded-lg bg-background border border-border/20 px-3 py-2 text-sm text-foreground"
            >
              <option value="">Select employee…</option>
              {employees.map((emp) => (
                <option key={emp.email} value={emp.email}>
                  {emp.name} ({emp.email})
                </option>
              ))}
            </select>
            <select
              value={pickRaci}
              onChange={(e) => setPickRaci(e.target.value)}
              className="rounded-lg bg-background border border-border/20 px-3 py-2 text-sm text-foreground"
            >
              {Object.entries(RACI_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {k} - {label}
                </option>
              ))}
            </select>
            <Button type="submit" className="btn-glow-primary gap-1.5 flex-none" disabled={busy || !pickEmail}>
              <Plus size={15} /> Add
            </Button>
          </form>
        </>
      )}
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
    </GlassCard>
  );
};

const PhotoReminderCard = () => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const remind = () => {
    setBusy(true);
    setError("");
    setResult(null);
    axios
      .post(`${API}/employees/admin/remind-missing-photos`, {}, { withCredentials: true, timeout: 60000 })
      .then((res) => setResult(res.data))
      .catch((err) => setError(err.response?.data?.detail || "Could not send reminders."))
      .finally(() => setBusy(false));
  };

  return (
    <GlassCard className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <Mail size={20} className="text-qcyan" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Missing Profile Photos</h3>
          <p className="text-xs text-muted-foreground">
            Emails everyone who's completed their Employee Code but hasn't added a photo yet, with a direct link to
            add one.
          </p>
        </div>
      </div>
      <Button onClick={remind} disabled={busy} className="btn-glow-primary gap-1.5">
        <Mail size={15} /> {busy ? "Sending…" : "Send Reminders"}
      </Button>
      {result && (
        <p className="text-xs text-muted-foreground mt-3">
          Reminded {result.reminded.length} {result.skipped.length > 0 && `(${result.skipped.length} could not be emailed)`}
        </p>
      )}
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
    </GlassCard>
  );
};

const RefreshCacheCard = () => {
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [failedSites, setFailedSites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = () => {
    setBusy(true);
    setError("");
    setFailedSites([]);
    axios
      .post(`${API}/sustainability/refresh-cache`, {}, { withCredentials: true, timeout: 60000 })
      .then((res) => {
        setLastRefreshed(res.data.fetched_at);
        setFailedSites(res.data.failed_sites || []);
      })
      .catch((err) => setError(err.response?.data?.detail || "Could not refresh the cache."))
      .finally(() => setBusy(false));
  };

  return (
    <GlassCard className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <RefreshCw size={20} className="text-qcyan" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Sustainability Data Cache</h3>
          <p className="text-xs text-muted-foreground">
            The public dashboard serves cached sheet data, refreshed hourly automatically. Use this after loading
            new data into a site's sheet to see it reflected immediately.
          </p>
        </div>
      </div>
      <Button onClick={refresh} disabled={busy} className="btn-glow-primary gap-1.5">
        <RefreshCw size={15} /> {busy ? "Refreshing…" : "Refresh Now"}
      </Button>
      {lastRefreshed && (
        <p className="text-xs text-muted-foreground mt-3">Refreshed at {formatDateTime(lastRefreshed)}</p>
      )}
      {failedSites.length > 0 && (
        <div className="text-sm text-destructive mt-3">
          <p className="font-medium">{failedSites.length} site(s) failed to refresh (excluded from totals):</p>
          <ul className="list-disc list-inside text-xs mt-1">
            {failedSites.map((s) => (
              <li key={s.site_id}>
                {s.name}: {s.error}
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-1">Try Refresh Now again in a minute or two.</p>
        </div>
      )}
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
    </GlassCard>
  );
};

const MediaCacheCard = () => {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = () => {
    setBusy(true);
    setError("");
    setResult(null);
    axios
      .post(`${API}/media/refresh-cache`, {}, { withCredentials: true, timeout: 60000 })
      .then((res) => setResult(res.data))
      .catch((err) => setError(err.response?.data?.detail || `${err.name}: ${err.message}`))
      .finally(() => setBusy(false));
  };

  return (
    <GlassCard className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <Newspaper size={20} className="text-qcyan" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Media Cache</h3>
          <p className="text-xs text-muted-foreground">
            The Media, Investors and Employees news feeds serve stored Google News headlines, refreshed hourly
            automatically. Use this to pull the latest headlines immediately.
          </p>
        </div>
      </div>
      <Button onClick={refresh} disabled={busy} className="btn-glow-primary gap-1.5">
        <RefreshCw size={15} /> {busy ? "Refreshing…" : "Refresh Now"}
      </Button>
      {result && (
        <p className="text-xs text-muted-foreground mt-3">
          Refreshed at {formatDateTime(result.fetched_at)}: {formatNumber(result.items)} items stored (
          {formatNumber(result.new_items)} new)
        </p>
      )}
      {result?.errors?.length > 0 && (
        <ul className="text-xs text-destructive mt-2 list-disc list-inside">
          {result.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
    </GlassCard>
  );
};

const DocumentMirrorCard = () => {
  const [status, setStatus] = useState(null);
  const [serviceAccount, setServiceAccount] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/admin/documents/mirror-status`, { withCredentials: true })
      .then((res) => setStatus(res.data))
      .catch(() => {});
    axios
      .get(`${API}/admin/documents/drive-service-account`, { withCredentials: true })
      .then((res) => setServiceAccount(res.data))
      .catch(() => {});
  }, []);

  const refresh = () => {
    setBusy(true);
    setError("");
    setResult(null);
    axios
      .post(`${API}/admin/documents/refresh-drive-mirror`, {}, { withCredentials: true, timeout: 300000 })
      .then((res) => {
        setResult(res.data);
        setStatus(res.data);
      })
      .catch((err) => setError(err.response?.data?.detail || `${err.name}: ${err.message}`))
      .finally(() => setBusy(false));
  };

  const shown = result || status;

  return (
    <GlassCard className="p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25">
          <FolderSync size={20} className="text-qcyan" />
        </span>
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">Document Mirror (Drive)</h3>
          <p className="text-xs text-muted-foreground">
            Mirrors qualitypower.com's uploaded media and Screener's QPOWER Documents tab into Google Drive,
            weekly and automatically. Super admin only.
          </p>
        </div>
      </div>
      {serviceAccount && (
        <p className="text-xs text-muted-foreground mb-3">
          Target Drive folder: <span className="font-mono">{serviceAccount.drive_folder_id}</span>. Share it with{" "}
          <span className="font-mono">{serviceAccount.email}</span> as Content Manager if you haven't already -
          uploads will fail until that's done.
        </p>
      )}
      <Button onClick={refresh} disabled={busy} className="btn-glow-primary gap-1.5">
        <RefreshCw size={15} /> {busy ? "Refreshing…" : "Refresh Now"}
      </Button>
      {shown?.fetched_at && (
        <p className="text-xs text-muted-foreground mt-3">
          Refreshed at {formatDateTime(shown.fetched_at)}: website {formatNumber(shown.website_uploaded || 0)}{" "}
          uploaded ({formatNumber(shown.website_skipped || 0)} already present), Screener{" "}
          {formatNumber(shown.screener_uploaded || 0)} uploaded ({formatNumber(shown.screener_skipped || 0)}{" "}
          already present).
        </p>
      )}
      {shown?.errors?.length > 0 && (
        <ul className="text-xs text-destructive mt-2 list-disc list-inside">
          {shown.errors.map((e, i) => (
            <li key={i}>
              {e.source}: {e.url} - {e.error}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
    </GlassCard>
  );
};

const Admin = () => {
  const { user } = useAuth();

  if (!user?.is_admin) {
    return <Navigate to="/employees" replace />;
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Admin is moving to the QPi admin site ({" "}
        <a href={ADMIN_URL} target="_blank" rel="noopener noreferrer" className="text-qcyan">
          {ADMIN_URL.replace("https://", "")}
        </a>
        ), where these cards are under IT and Sustainability.
      </p>
      <div className="grid md:grid-cols-2 gap-6 items-start">
        <Reveal>
          <SiteList />
        </Reveal>
        <Reveal delay={0.06}>
          <AdminList />
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 gap-6 items-start">
        <Reveal delay={0.1}>
          <RaciCard />
        </Reveal>
        <Reveal delay={0.14}>
          <PhotoReminderCard />
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 gap-6 items-start">
        <Reveal delay={0.18}>
          <RefreshCacheCard />
        </Reveal>
        <Reveal delay={0.22}>
          <MediaCacheCard />
        </Reveal>
      </div>
      {user?.is_super_admin && (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <Reveal delay={0.26}>
            <DocumentMirrorCard />
          </Reveal>
        </div>
      )}
    </div>
  );
};

export default Admin;

// Reused by the QPi admin site's IT and Sustainability sections.
export { AdminList, SiteList, RefreshCacheCard, MediaCacheCard, RaciCard, PhotoReminderCard, DocumentMirrorCard };
